/* eslint-disable @typescript-eslint/no-explicit-any -- existing annotations; retyping would change compile behavior */
import { useEffect, useMemo, useState } from 'react';
import { Autocomplete, Box, CircularProgress, IconButton, TextField } from '@mui/material';
import { Close } from '@mui/icons-material';
import { Bar } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title as ChartTitle,
    Tooltip as ChartTooltip,
    Legend,
} from 'chart.js';
import { formatCurrency } from 'utils/formatters';
import {
    AnalyticsServiceOption,
    getAnalyticsServices,
    getServicePriceHistogram,
} from 'api/rest/analytics';
import { useAuth } from 'contexts/ProtectedRoute';
import { Role } from 'data/constants/roles';
import { ServicePriceHistogramData, ServicePriceHistogramServiceStat } from 'hooks/analytics';
import { AnalyticsColors, stateMessageSx, loadingWrapSx } from '../../components/analyticsUi';
import CollapsibleSection from '../../components/CollapsibleSection';
import { REVENUE_SERVICE_PRICE_HISTOGRAM } from '../../constants/metricDescriptions';

ChartJS.register(CategoryScale, LinearScale, BarElement, ChartTitle, ChartTooltip, Legend);

const BUCKET_COUNT = 12;

const formatPriceShort = (n: number) => {
    if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000) return `$${Math.round(n / 1_000)}k`;
    return `$${Math.round(n)}`;
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface ServicePriceHistogramProps {
    from?: string;
    to?: string;
    serviceProviderId?: string;
}

const ServicePriceHistogram = ({ from, to, serviceProviderId }: ServicePriceHistogramProps) => {
    const { role } = useAuth();
    const isSuperSuperAdmin = role === Role.SUPER_SUPER_ADMIN;

    // Provider override is only honored for SUPER_SUPER_ADMIN; for every other
    // admin role the backend scopes by the caller's own service provider.
    const providerScope = isSuperSuperAdmin ? serviceProviderId : undefined;

    // Catalog (service names for the picker)
    const [catalog, setCatalog] = useState<AnalyticsServiceOption[]>([]);
    const [catalogLoading, setCatalogLoading] = useState<boolean>(true);
    const [catalogError, setCatalogError] = useState<string | null>(null);

    // Selection
    const [selectedNames, setSelectedNames] = useState<string[]>([]);

    // Histogram payload
    const [histogramData, setHistogramData] = useState<ServicePriceHistogramData | null>(null);
    const [histogramLoading, setHistogramLoading] = useState<boolean>(false);
    const [histogramError, setHistogramError] = useState<string | null>(null);

    // ---- Catalog fetch ----
    // For SUPER_SUPER_ADMIN: refetches when the selected provider changes.
    // For every other role: providerScope is undefined and stable, so this
    // runs once and stays put.
    useEffect(() => {
        let cancelled = false;
        setCatalogLoading(true);
        setCatalogError(null);
        getAnalyticsServices(providerScope)
            .then((res) => {
                if (cancelled) return;
                setCatalog(res.data ?? []);
            })
            .catch((err) => {
                if (cancelled) return;
                setCatalogError(
                    err?.response?.data?.message || err.message || 'Failed to load services.'
                );
                setCatalog([]);
            })
            .finally(() => {
                if (!cancelled) setCatalogLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [providerScope]);

    // Drop selected names that fell out of the catalog after a refetch
    // (e.g. SUPER_SUPER_ADMIN switched service provider).
    useEffect(() => {
        if (catalogLoading) return;
        setSelectedNames((prev) => {
            const next = prev.filter((name) => catalog.includes(name));
            return next.length === prev.length ? prev : next;
        });
    }, [catalog, catalogLoading]);

    // ---- Histogram fetch ----
    useEffect(() => {
        if (selectedNames.length === 0) {
            setHistogramData(null);
            setHistogramError(null);
            setHistogramLoading(false);
            return undefined;
        }
        let cancelled = false;
        setHistogramLoading(true);
        setHistogramError(null);
        getServicePriceHistogram(selectedNames, from, to, providerScope, BUCKET_COUNT)
            .then((res) => {
                if (cancelled) return;
                setHistogramData(res.data);
            })
            .catch((err) => {
                if (cancelled) return;
                setHistogramError(
                    err?.response?.data?.message || err.message || 'Failed to load price histogram.'
                );
                setHistogramData(null);
            })
            .finally(() => {
                if (!cancelled) setHistogramLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [selectedNames, from, to, providerScope]);

    // ---- Derived ----
    const availableOptions = useMemo(
        () => catalog.filter((name) => !selectedNames.includes(name)),
        [catalog, selectedNames]
    );

    // Prefer the canonical list from the response so we render whatever the
    // backend actually used. Fall back to local selection while the histogram
    // is in flight (or before the first response).
    const displayedNames = useMemo(
        () => histogramData?.filters.serviceNames ?? selectedNames,
        [histogramData, selectedNames]
    );

    const serviceStatByName = useMemo(() => {
        const map = new Map<string, ServicePriceHistogramServiceStat>();
        histogramData?.services.forEach((s) => map.set(s.name, s));
        return map;
    }, [histogramData]);

    const handleAdd = (name: string | null) => {
        if (!name) return;
        setSelectedNames((prev) => (prev.includes(name) ? prev : [...prev, name]));
    };

    const handleRemove = (name: string) => {
        setSelectedNames((prev) => prev.filter((n) => n !== name));
    };

    // ---- Chart ----
    const chartData = useMemo(() => {
        const buckets = histogramData?.histogram.buckets ?? [];
        return {
            labels: buckets.map((b) =>
                b.rangeMin === b.rangeMax
                    ? formatPriceShort(b.rangeMin)
                    : `${formatPriceShort(b.rangeMin)}–${formatPriceShort(b.rangeMax)}`
            ),
            datasets: [
                {
                    label: 'Cases',
                    data: buckets.map((b) => b.count),
                    backgroundColor: (ctx: any) => {
                        const chart = ctx.chart;
                        const { ctx: canvasCtx, chartArea } = chart;
                        if (!chartArea) return 'rgba(37, 99, 235, 0.6)';
                        const gradient = canvasCtx.createLinearGradient(
                            0,
                            chartArea.top,
                            0,
                            chartArea.bottom
                        );
                        gradient.addColorStop(0, 'rgba(37, 99, 235, 0.85)');
                        gradient.addColorStop(1, 'rgba(37, 99, 235, 0.35)');
                        return gradient;
                    },
                    borderColor: '#2563EB',
                    borderWidth: 1.5,
                    borderRadius: 4,
                    hoverBackgroundColor: '#2563EB',
                    maxBarThickness: 48,
                },
            ],
        };
    }, [histogramData]);

    const chartOptions = useMemo(
        () => ({
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                legend: { display: false },
                tooltip: {
                    backgroundColor: '#1F2937',
                    titleFont: { size: 12, weight: '600' as const },
                    bodyFont: { size: 13 },
                    padding: 10,
                    cornerRadius: 8,
                    callbacks: {
                        title: (items: any[]) => items[0]?.label ?? '',
                        label: (ctx: any) => ` ${ctx.raw} ${ctx.raw === 1 ? 'case' : 'cases'}`,
                    },
                },
            },
            scales: {
                y: {
                    beginAtZero: true,
                    ticks: {
                        precision: 0,
                        font: { size: 11 },
                        color: '#9CA3AF',
                    },
                    grid: { color: '#F3F4F6' },
                    border: { display: false },
                    title: {
                        display: true,
                        text: 'Cases',
                        color: '#6B7280',
                        font: { size: 11, weight: '600' as const },
                    },
                },
                x: {
                    grid: { display: false },
                    ticks: {
                        font: { size: 11 },
                        color: '#6B7280',
                        maxRotation: 0,
                        autoSkip: true,
                    },
                    border: { display: false },
                    title: {
                        display: true,
                        text: 'Price',
                        color: '#6B7280',
                        font: { size: 11, weight: '600' as const },
                    },
                },
            },
        }),
        []
    );

    const summary = histogramData?.summary;
    const hasCases = !!summary && summary.totalCases > 0;

    return (
        <CollapsibleSection
            id="service-price-histogram"
            title="Service Price Histogram"
            tooltip={REVENUE_SERVICE_PRICE_HISTOGRAM}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem',
                    flexWrap: 'wrap',
                    mb: '1rem',
                }}
            >
                <Autocomplete<string, false, false, false>
                    options={availableOptions}
                    getOptionLabel={(option) => option}
                    value={null}
                    onChange={(_, value) => handleAdd(value)}
                    isOptionEqualToValue={(option, value) => option === value}
                    loading={catalogLoading}
                    disabled={catalogLoading || !!catalogError}
                    blurOnSelect
                    clearOnBlur
                    sx={{ minWidth: 280, maxWidth: 360, flexGrow: 1 }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            size="small"
                            label="Add service"
                            placeholder={
                                catalogLoading ? 'Loading services...' : 'Search services...'
                            }
                            slotProps={{
                                ...params.slotProps,
                                input: {
                                    ...params.slotProps.input,
                                    endAdornment: (
                                        <>
                                            {catalogLoading ? <CircularProgress size={16} /> : null}
                                            {params.slotProps.input.endAdornment}
                                        </>
                                    ),
                                },
                            }}
                        />
                    )}
                />
                {(from || to) && (
                    <Box
                        component="span"
                        sx={{
                            fontSize: 12,
                            fontWeight: 500,
                            color: AnalyticsColors.textSecondary,
                            background: AnalyticsColors.surfaceBg,
                            border: `1px solid ${AnalyticsColors.borderLight}`,
                            borderRadius: '999px',
                            p: '4px 10px',
                            fontVariantNumeric: 'tabular-nums',
                        }}
                    >
                        {from || '—'} → {to || '—'}
                    </Box>
                )}
            </Box>

            {catalogError && (
                <Box
                    sx={{
                        p: '0.75rem 1rem',
                        mb: '1rem',
                        background: '#fee2e2',
                        border: '1px solid #fca5a5',
                        borderRadius: '8px',
                        color: '#991b1b',
                        fontSize: 13,
                    }}
                >
                    {catalogError}
                </Box>
            )}

            {displayedNames.length === 0 ? (
                <Box sx={stateMessageSx}>
                    {catalogError
                        ? 'Service catalog unavailable.'
                        : 'Select one or more services to see their price distribution.'}
                </Box>
            ) : (
                <>
                    <Box
                        component="ul"
                        sx={{
                            listStyle: 'none',
                            m: '0 0 1.25rem 0',
                            p: 0,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '6px',
                        }}
                    >
                        {displayedNames.map((name) => {
                            const stat = serviceStatByName.get(name);
                            return (
                                <Box
                                    component="li"
                                    key={name}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '12px',
                                        p: '8px 12px',
                                        background: AnalyticsColors.surfaceBg,
                                        border: `1px solid ${AnalyticsColors.borderLight}`,
                                        borderRadius: '8px',
                                    }}
                                >
                                    <Box
                                        component="span"
                                        sx={{
                                            fontSize: '13.5px',
                                            fontWeight: 600,
                                            color: AnalyticsColors.textPrimary,
                                        }}
                                    >
                                        {name}
                                    </Box>
                                    {stat && (
                                        <Box
                                            component="span"
                                            sx={{
                                                ml: 'auto',
                                                fontSize: 12,
                                                color: AnalyticsColors.textSecondary,
                                                fontVariantNumeric: 'tabular-nums',
                                            }}
                                        >
                                            {stat.caseCount} cases · avg{' '}
                                            {formatCurrency(Math.round(stat.averagePrice))}
                                        </Box>
                                    )}
                                    <IconButton
                                        size="small"
                                        onClick={() => handleRemove(name)}
                                        aria-label={`Remove ${name}`}
                                    >
                                        <Close sx={{ fontSize: 14 }} />
                                    </IconButton>
                                </Box>
                            );
                        })}
                    </Box>

                    {histogramLoading ? (
                        <Box sx={loadingWrapSx}>
                            <CircularProgress size={28} />
                        </Box>
                    ) : histogramError ? (
                        <Box sx={stateMessageSx}>{histogramError}</Box>
                    ) : hasCases && summary ? (
                        <>
                            <Box
                                sx={{
                                    display: 'flex',
                                    gap: '2rem',
                                    flexWrap: 'wrap',
                                    p: '0.75rem 1rem',
                                    mb: '0.75rem',
                                    background: AnalyticsColors.surfaceBg,
                                    border: `1px solid ${AnalyticsColors.borderLight}`,
                                    borderRadius: '10px',
                                }}
                            >
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                    <Box
                                        component="span"
                                        sx={{
                                            fontSize: 11,
                                            fontWeight: 600,
                                            color: AnalyticsColors.textTertiary,
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px',
                                        }}
                                    >
                                        Total cases
                                    </Box>
                                    <Box
                                        component="span"
                                        sx={{
                                            fontSize: 20,
                                            fontWeight: 700,
                                            color: AnalyticsColors.textPrimary,
                                            fontVariantNumeric: 'tabular-nums',
                                            lineHeight: 1.1,
                                        }}
                                    >
                                        {summary.totalCases}
                                    </Box>
                                </Box>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                    <Box
                                        component="span"
                                        sx={{
                                            fontSize: 11,
                                            fontWeight: 600,
                                            color: AnalyticsColors.textTertiary,
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px',
                                        }}
                                    >
                                        Average price
                                    </Box>
                                    <Box
                                        component="span"
                                        sx={{
                                            fontSize: 20,
                                            fontWeight: 700,
                                            color: AnalyticsColors.textPrimary,
                                            fontVariantNumeric: 'tabular-nums',
                                            lineHeight: 1.1,
                                        }}
                                    >
                                        {formatCurrency(Math.round(summary.averagePrice))}
                                    </Box>
                                </Box>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                    <Box
                                        component="span"
                                        sx={{
                                            fontSize: 11,
                                            fontWeight: 600,
                                            color: AnalyticsColors.textTertiary,
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px',
                                        }}
                                    >
                                        Median price
                                    </Box>
                                    <Box
                                        component="span"
                                        sx={{
                                            fontSize: 20,
                                            fontWeight: 700,
                                            color: AnalyticsColors.textPrimary,
                                            fontVariantNumeric: 'tabular-nums',
                                            lineHeight: 1.1,
                                        }}
                                    >
                                        {formatCurrency(Math.round(summary.medianPrice))}
                                    </Box>
                                </Box>
                            </Box>

                            <Box sx={{ height: 260, mt: '0.5rem' }}>
                                <Bar data={chartData} options={chartOptions as any} />
                            </Box>
                        </>
                    ) : (
                        <Box sx={stateMessageSx}>
                            No cases for the selected services in this period.
                        </Box>
                    )}
                </>
            )}
        </CollapsibleSection>
    );
};

export default ServicePriceHistogram;
